import type { FunctionComponent } from 'react'
import React from 'react'
import { Dropdown } from '@which/seatbelt'
import type { FilterData } from '@which/shared'
import { dynamicDatalayerPush } from '@which/shared'

import type { Filter as FilterType } from '../../../../../generated/frontend'
import type {
  FilterCallback,
  FilterItemIntersection,
} from '../../../../../shared/types/ReviewsFilters'
import styles from './DropdownRange.module.scss'

export const DropdownRange: FunctionComponent<DropdownRangeProps> = ({
  filterLabel,
  filterItems,
  filterCallback = () => null,
  externalId,
  basicFilterData = {},
  categorySlug,
}) => {
  const filterDataValue = basicFilterData[externalId]
  const valueTo = filterDataValue?.to?.[0] || ''
  const valueFrom = filterDataValue?.from?.[0] || ''

  const callbackMin = (value: string) => {
    const selectedItemMin = filterItems.find((item) => `${item.from}` === value)
    dynamicDatalayerPush({
      eventAction: `${categorySlug} - ${filterLabel} - From`,
      eventCategory: 'refine results',
      eventLabel: `${selectedItemMin?.label ?? 'min'}`,
    })
    filterCallback({ path: `${externalId}.from`, filterValue: value ? [value] : [] })
  }

  const callbackMax = (value: string) => {
    const selectedItemMax = filterItems.find((item) => `${item.to}` === value)
    dynamicDatalayerPush({
      eventAction: `${categorySlug} - ${filterLabel} - To`,
      eventCategory: 'refine results',
      eventLabel: `${selectedItemMax?.label ?? 'max'}`,
    })
    filterCallback({ path: `${externalId}.to`, filterValue: value ? [value] : [] })
  }

  return (
    <>
      <Dropdown
        aria-label={`${filterLabel} min`}
        name={`${filterLabel}-Min`}
        options={createOptions(filterItems, 'Min')}
        callback={callbackMin}
        value={valueFrom}
      />
      <span className={styles.operator}>to</span>
      <Dropdown
        aria-label={`${filterLabel} max`}
        name={`${filterLabel}-Max`}
        options={createOptions(filterItems, 'Max')}
        callback={callbackMax}
        value={valueTo}
      />
    </>
  )
}

///////IMPLEMENTATION/////////

type DropdownRangeProps = {
  filterLabel: FilterType['filterLabel']
  filterItems: FilterItemIntersection[]
  filterCallback?: (options: FilterCallback) => void
  externalId: string
  basicFilterData?: FilterData
  categorySlug: string
}

const createOptions = (filterItems: FilterItemIntersection[], filterLabel: 'Min' | 'Max') => {
  const mappedItems = filterItems.map(({ label = '', from, to }) => {
    const value = filterLabel === 'Min' ? from : to
    return {
      label,
      value: `${value}`,
    }
  })

  return [{ value: '', label: filterLabel }, ...mappedItems]
}
